import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  MAC_DMG_URL,
  MIN_MAC_VERSION_FOR_PASSKEYS,
  STORAGE_KEYS,
} from '@/lib/config'
import {
  getDesktopVersion,
  isDesktopShell,
  isVersionLessThan,
  openExternalUrl,
} from '@/lib/desktopShell'

const DISMISS_COOLDOWN_DAYS = 30
const TOAST_ID = 'mac-update-available'
const SHOWN_SESSION_KEY = 'mac_update_shown_this_session'

/**
 * Notifie l'utilisateur d'une app Mac obsolète via un toast persistant.
 *
 * Pattern volontairement générique (versions futures) : on compare la version
 * du shell à un seuil minimum, et on propose le téléchargement du dernier
 * .dmg. Le seuil actuel est aligné sur l'introduction des passkeys mais peut
 * être bumpé à chaque release majeure suivante.
 *
 * - Hors shell Mac : no-op
 * - Shell Mac avec version récente : no-op
 * - Skip mémorisé 30 jours après dismiss
 */
export function MacUpdateNotifier() {
  const { t } = useTranslation()

  useEffect(() => {
    if (!isDesktopShell()) return
    if (sessionStorage.getItem(SHOWN_SESSION_KEY)) return

    const version = getDesktopVersion()
    // Pas de version détectable → on ne fait rien (probablement un shell tiers
    // ou un test : on évite les faux positifs)
    if (!version) return
    if (!isVersionLessThan(version, MIN_MAC_VERSION_FOR_PASSKEYS)) return

    const dismissed = localStorage.getItem(STORAGE_KEYS.MAC_UPDATE_DISMISSED_AT)
    if (dismissed) {
      const elapsedMs = Date.now() - Number.parseInt(dismissed, 10)
      const cooldownMs = DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000
      if (Number.isFinite(elapsedMs) && elapsedMs < cooldownMs) return
    }

    // Léger délai pour ne pas se déclencher pendant le 1er paint du dashboard
    const timer = window.setTimeout(() => {
      sessionStorage.setItem(SHOWN_SESSION_KEY, '1')
      toast(
        t('macUpdate.title', 'Une nouvelle version de Callizy est disponible'),
        {
          id: TOAST_ID,
          duration: Infinity,
          description: t(
            'macUpdate.description',
            'Mettez à jour pour profiter des dernières fonctionnalités.',
          ),
          action: {
            label: t('macUpdate.download', 'Télécharger'),
            onClick: () => {
              openExternalUrl(MAC_DMG_URL)
              // On dismiss aussi : l'user a cliqué, pas la peine de re-spam
              localStorage.setItem(
                STORAGE_KEYS.MAC_UPDATE_DISMISSED_AT,
                String(Date.now()),
              )
              toast.dismiss(TOAST_ID)
            },
          },
          onDismiss: () => {
            localStorage.setItem(
              STORAGE_KEYS.MAC_UPDATE_DISMISSED_AT,
              String(Date.now()),
            )
          },
          onAutoClose: () => {
            // duration: Infinity → en théorie ne s'auto-ferme pas, mais on
            // mémorise quand même par sécurité.
            localStorage.setItem(
              STORAGE_KEYS.MAC_UPDATE_DISMISSED_AT,
              String(Date.now()),
            )
          },
        },
      )
    }, 1200)

    return () => {
      window.clearTimeout(timer)
    }
  }, [t])

  return null
}
